import React from "react";
import AsideItem from "@components/AsideItem/AsideItem";
import FilterBySearch from "@components/FilterBySearch/FilterBySearch";
import { Switch } from "antd";
import { ArchiveIcon } from "@components/CustomIcons/CustomIcons";
import Aside from "@components/Aside/Aside";
import { DataAttributes, DataAttributesBuilder } from "@services/DataAttributesBuilder";
import { defineMessages, InjectedIntlProps, injectIntl } from "react-intl";
import styles from "./index.module.scss";
import { inject, observer } from "mobx-react";
import { ChannelsStoreModel } from "@stores/channelsStore/channelsStoreModel";
import { ChannelsUrlParams } from "@stores/channelsStore/channelsStore";

const labels = defineMessages({
  filterByNamePlaceholder: {
    id: "PublishingChannelListFilters.filterByNamePlaceholder",
    defaultMessage: "Filter by name",
  },
  filterByOnlyArchived: {
    id: "PublishingChannelListFilters.filterByOnlyArchived",
    defaultMessage: "Archived only",
  },
  filterByOnlyUnarchived: {
    id: "PublishingChannelListFilters.filterByOnlyUnarchived",
    defaultMessage: "Unarchived only",
  },
  removeAllFilters: {
    id: "PublishingChannelListFilters.removeAllFilters",
    defaultMessage: "Remove all filters",
  },
});

interface PublishingChannelListFiltersProps extends InjectedIntlProps {}

interface InjectedProps extends PublishingChannelListFiltersProps {
  channelsStore: ChannelsStoreModel;
}

@inject("channelsStore")
@observer
class PublishingChannelListFilters extends React.Component<PublishingChannelListFiltersProps> {
  get injected() {
    return this.props as InjectedProps;
  }

  createSearchDataAttributes = (): DataAttributes => {
    return new DataAttributesBuilder("repositories").setSearchAttribute("search-repository").build();
  };

  onSearchByName = (value?: string) => {
    if (!!value && value.trim().length > 0) {
      this.injected.channelsStore.searchParams.setUrlParam(ChannelsUrlParams.name, value);
    } else {
      this.injected.channelsStore.searchParams.deleteUrlParam(ChannelsUrlParams.name);
    }
    this.injected.channelsStore.setChannels();
  };

  onSearchByArchived = (value: boolean) => {
    if (value) {
      this.injected.channelsStore.searchParams.setUrlParam(ChannelsUrlParams.archived, "true");
    } else {
      this.injected.channelsStore.searchParams.deleteUrlParam(ChannelsUrlParams.archived);
    }
    this.injected.channelsStore.setChannels();
  };

  onResetFilters = () => {
    this.injected.channelsStore.searchParams.deleteAllUrlParams();
    this.injected.channelsStore.setChannels();
  };

  render() {
    const {
      searchParams: { usableSearchParams },
    } = this.injected.channelsStore;
    return (
      <Aside>
        <AsideItem key={"FilterByName"}>
          <FilterBySearch
            dataAttributes={this.createSearchDataAttributes}
            placeholder={this.props.intl.formatMessage(labels.filterByNamePlaceholder)}
            defaultValue={usableSearchParams && usableSearchParams.name}
            onSearch={this.onSearchByName}
          />
        </AsideItem>
        <AsideItem key={"FilterByArchiveStatus"}>
          <Switch
            style={{ marginRight: 10 }}
            onChange={this.onSearchByArchived}
            checked={usableSearchParams && usableSearchParams.archived === "true"}
          />
          {usableSearchParams.archived ? (
            <React.Fragment>
              <span>{this.props.intl.formatMessage(labels.filterByOnlyArchived)}</span>
              <ArchiveIcon className={styles.ArchiveIconStyle} />
            </React.Fragment>
          ) : (
            <span>{this.props.intl.formatMessage(labels.filterByOnlyUnarchived)}</span>
          )}
        </AsideItem>
        {Object.keys(usableSearchParams).length > 0 && (
          <AsideItem key="RemoveAllAppliedFilters">
            <a onClick={this.onResetFilters}>{this.props.intl.formatMessage(labels.removeAllFilters)}</a>
          </AsideItem>
        )}
      </Aside>
    );
  }
}

export default injectIntl(PublishingChannelListFilters);
